// src/hooks/useApi.js
import { useCallback } from "react";
import axios from "axios";
import { API_BASE_URL, AUTH_BASE_URL, CLIENT_HEADER } from "../config";

export const useApi = () => {
  // isFormData = true -> don't set Content-Type (let axios set multipart/form-data + boundary)
  // suppressAuthRedirect: for background/polling calls — on an auth error, throw to the caller
  // instead of clearing the session and bouncing through OIDC (which turns a transient poll 401
  // into an infinite re-login loop). Genuine session loss is still handled on the next foreground call.
  const apiCall = useCallback(async (method, url, data = null, isFormData = false, responseType = null, suppressAuthRedirect = false) => {
    // Get Organization-Id from localStorage
    let orgIdHeader = {};
    const selectedOrg = localStorage.getItem("selectedOrganization");
    if (selectedOrg) {
      try {
        const org = JSON.parse(selectedOrg);
        if (org.organizationId) {
          orgIdHeader = { "Organization-Id": org.organizationId };
        }
      } catch (e) {
        // Invalid JSON, ignore
      }
    }

    // Get Tenant-Id from localStorage
    let tenantIdHeader = {};
    const selectedTenantId = localStorage.getItem("selectedTenantId");
    if (selectedTenantId) {
      tenantIdHeader = { "Tenant-Id": selectedTenantId };
    }

    const config = {
      method,
      url: `${API_BASE_URL}${url}`,
      withCredentials: true,   // send the HttpOnly session cookie
      headers: {
        [CLIENT_HEADER]: "1",
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...orgIdHeader,
        ...tenantIdHeader
      },
      ...(data !== null ? { data } : {}),
      ...(responseType ? { responseType } : {})
    };

    try {
      const response = await axios(config);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        if (suppressAuthRedirect) throw error;

        const code = error.response?.data?.error;
        const msg = (error.response?.data?.message || "").toLowerCase();

        // Authenticated but no organization in context (org-scoped call with no selected org —
        // e.g. after an idle logout cleared it). NOT a dead session: re-auth would just loop back
        // to the same page. Route to the org picker instead (but not when already on it).
        if ((code === "organization_required" || msg.includes("organization")) &&
            !window.location.pathname.startsWith("/organizations")) {
          localStorage.removeItem("selectedOrganization");
          window.location.href = "/organizations";
          return;
        }

        // Session missing/expired — send the browser through OIDC sign-in. Do NOT clear app storage:
        // the re-auth round-trip + /Auth/validate re-bootstrap refreshes auth state, and wiping
        // selectedOrganization here breaks deep links (page returns with no org context → blank).
        if (!window.__redirectingToLogin) {
          window.__redirectingToLogin = true;
          window.location.href = `${AUTH_BASE_URL}/auth/login?returnUrl=${encodeURIComponent(window.location.href)}`;
        }
        return;
      }
      // Handle 403 for organization access revoked
      if (error.response?.status === 403 &&
          error.response?.data?.message?.toLowerCase().includes("organization")) {
        if (suppressAuthRedirect) throw error;
        localStorage.removeItem("selectedOrganization");
        window.location.href = "/organizations";
        return;
      }
      throw error;
    }
  }, []);

  return apiCall;
};
